import { useEffect, useState } from "react";
import { findCountryFlag } from "../../Team/countries";
import { TEMP_SPECIAL_POINTS } from "../../Team/TeamView";
import { TournamentLink } from "../../Tournament/TournamentLink";
import { getWinnerId } from "../../Tournament/TournamentView";
import { request } from "../../../../network/Request";
import { useTranslation } from "react-i18next";
import { useYear } from "../../../../context/YearContext.js";
import StatusMessage from "../../../../components/StatusMessage/StatusMessage.js";

// 2025 결과 (서버에 국가 정보가 없어 직접 지정)
// 각 거리별 남자 개인전 우승자
const maleWinners2025 = {
  "145M": [
    { rank: 1, country: "RU" },
    { rank: 2, country: "KR" },
    { rank: 3, country: "KR" },
  ],
  "90M": [
    { rank: 1, country: "TH" },
    { rank: 2, country: "MY" },
    { rank: 3, country: "AU" },
  ],
  "70M": [
    { rank: 1, country: "MN" },
    { rank: 2, country: "KZ" },
    { rank: 3, country: "KZ" },
  ],
  "30M": [
    { rank: 1, country: "MN" },
    { rank: 2, country: "KZ" },
    { rank: 3, country: "ID" },
  ],
};

// 각 거리별 여자 개인전 우승자
const femaleWinners2025 = {
  "145M": [
    { rank: 1, country: "KR" },
    { rank: 2, country: "KR" },
    { rank: 3, country: "KR" },
  ],
  "90M": [
    { rank: 1, country: "HU" },
    { rank: 2, country: "AU" },
    { rank: 3, country: "MN" },
  ],
  "70M": [
    { rank: 1, country: "TR" },
    { rank: 2, country: "AU" },
    { rank: 3, country: "TR" },
  ],
  "30M": [
    { rank: 1, country: "KZ" },
    { rank: 2, country: "KZ" },
    { rank: 3, country: "KZ" },
  ],
};

// 각 거리별 단체전 우승 팀
const teamWinners2025 = {
  "145M": [
    { rank: 1, country: "KR" },
    { rank: 2, country: "KR" },
    { rank: 3, country: "KR" },
  ],
  "90M": [
    { rank: 1, country: "KZ" },
    { rank: 2, country: "AU" },
    { rank: 3, country: "HU" },
  ],
  "70M": [
    { rank: 1, country: "TR" },
    { rank: 2, country: "KZ" },
    { rank: 3, country: "KZ" },
  ],
  "30M": [
    { rank: 1, country: "ID" },
    { rank: 2, country: "MY" },
    { rank: 3, country: "RU" },
  ],
};

const WINNERS_2025 = {
  male: maleWinners2025,
  female: femaleWinners2025,
  team: teamWinners2025,
};

const DISTANCES = ["145", "90", "70", "30"];

// 2026~: 서버 결과로 거리별 1~3위 국가 계산
// 개인전: 결승 승자 1위·패자 2위, 3/4위전 승자 3위 (승자가 정해진 경기만)
const individualWinners = (tournament) => {
  const result = [];
  const final = tournament.round2[0];
  const finalWinner = getWinnerId(final);
  if (finalWinner) {
    final.players.forEach((p) =>
      result.push({ rank: p.player_id === finalWinner ? 1 : 2, country: p.country })
    );
  }
  const third = tournament.round3[0];
  const thirdWinner = getWinnerId(third);
  if (thirdWinner) {
    const p = third.players.find((p) => p.player_id === thirdWinner);
    result.push({ rank: 3, country: p.country });
  }
  return result;
};

// 단체전: 팀 총점 → 특별점수 순 (단체전 화면과 같은 기준), 동점은 같은 등수, 3위까지
const teamWinners = (teamScores, year, distance) => {
  if (!teamScores.some((player) => player.score != null)) return [];
  const tempSpecial = TEMP_SPECIAL_POINTS[`${year}-${distance}`] || {};
  const teams = {};
  teamScores.forEach((player) => {
    const team = (teams[player.team_name] ??= {
      country: player.country,
      special: player.team_special_point ?? tempSpecial[player.team_name] ?? 0,
      total: 0,
    });
    team.total += player.score || 0;
  });
  const sorted = Object.values(teams).sort(
    (a, b) => b.total - a.total || b.special - a.special
  );
  let rank = 1;
  return sorted
    .map((team, i) => {
      const prev = sorted[i - 1];
      if (prev && (prev.total !== team.total || prev.special !== team.special)) {
        rank = i + 1;
      }
      return { rank, country: team.country };
    })
    .filter((team) => team.rank <= 3);
};

const fetchWinners = async (year) => {
  const winners = { male: {}, female: {}, team: {} };
  await Promise.all(
    DISTANCES.flatMap((distance) => [
      ...["m", "f"].map(async (gender) => {
        const link = await new TournamentLink().request(gender, distance, year);
        winners[gender === "m" ? "male" : "female"][distance + "M"] =
          individualWinners(link.tournament);
      }),
      request({
        "bk-working": "team-score-list",
        distance: distance,
        target_type: "PRE",
        year: year,
      }).then(({ result, response }) => {
        winners.team[distance + "M"] = result
          ? teamWinners(response.team_score_list || [], year, distance)
          : [];
      }),
    ])
  );
  return winners;
};

const NationalView = () => {
  const { t } = useTranslation();
  const { year } = useYear();
  const [winners, setWinners] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    if (year === "2025") {
      setWinners(WINNERS_2025);
      return;
    }
    setWinners(null);
    let cancelled = false;
    fetchWinners(year)
      .then((w) => !cancelled && setWinners(w))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [year]);

  // 개인전 배점
  const INDIVIDUAL_POINTS = {
    1: 300,
    2: 200,
    3: 100,
  };

  // 단체전 배점
  const TEAM_POINTS = {
    1: 700,
    2: 600,
    3: 500,
  };

  // 국가별 점수 계산
  const calculateCountryScores = ({ male, female, team }) => {
    const countryScores = {};

    // 점수 추가 헬퍼 함수
    const addPoints = (country, distance, points, rank) => {
      if (!country) return;

      if (!countryScores[country]) {
        countryScores[country] = {
          country: country,
          scores: { "145M": 0, "90M": 0, "70M": 0, "30M": 0 },
          total: 0,
          gold: 0,
          silver: 0,
          bronze: 0,
        };
      }

      countryScores[country].scores[distance] += points;
      countryScores[country].total += points;

      // 메달 개수 추가
      if (rank === 1) countryScores[country].gold += 1;
      else if (rank === 2) countryScores[country].silver += 1;
      else if (rank === 3) countryScores[country].bronze += 1;
    };

    // 1. 남자 개인전 점수 추가
    Object.keys(male).forEach((distance) => {
      male[distance].forEach((result) => {
        const points = INDIVIDUAL_POINTS[result.rank] || 0;
        addPoints(result.country, distance, points, result.rank);
      });
    });

    // 2. 여자 개인전 점수 추가
    Object.keys(female).forEach((distance) => {
      female[distance].forEach((result) => {
        const points = INDIVIDUAL_POINTS[result.rank] || 0;
        addPoints(result.country, distance, points, result.rank);
      });
    });

    // 3. 단체전 점수 추가
    Object.keys(team).forEach((distance) => {
      team[distance].forEach((result) => {
        const points = TEAM_POINTS[result.rank] || 0;
        addPoints(result.country, distance, points, result.rank);
      });
    });

    // 총점 기준으로 정렬
    return Object.values(countryScores).sort((a, b) => {
      // 1. 총점 비교
      if (b.total !== a.total) {
        return b.total - a.total;
      }

      // 2. 동점일 때 금메달 개수 비교
      if (b.gold !== a.gold) {
        return b.gold - a.gold;
      }

      // 3. 금메달도 같으면 은메달 개수 비교
      if (b.silver !== a.silver) {
        return b.silver - a.silver;
      }

      // 4. 은메달도 같으면 동메달 개수 비교
      if (b.bronze !== a.bronze) {
        return b.bronze - a.bronze;
      }

      // 5. 메달도 모두 같으면 국가 코드 알파벳 순
      return a.country.localeCompare(b.country);
    });
  };

  // 순위 계산 함수 (1-3위는 항상 다른 순위)
  const getRankWithTopThree = (countries, index) => {
    // 1-3위는 항상 순차적으로 부여
    if (index < 3) {
      return index + 1;
    }

    // 4위부터는 동점자 처리
    const current = countries[index];

    // 점수가 다르면 새로운 순위
    let rank = index + 1;
    for (let i = index - 1; i >= 3; i--) {
      // 3위(index 2) 이후부터만 비교
      if (countries[i].total === current.total) {
        rank = i + 1;
      } else {
        break;
      }
    }

    return rank;
  };

  const rankedCountries = winners ? calculateCountryScores(winners) : [];

  return (
    <>
      <div className="table-box  box-1 margin-table">
        <div className="table-wrap">
          <div className="table-shadow">
            {error ? (
              <StatusMessage type="error" description={error} />
            ) : !winners ? (
              <StatusMessage type="loading" />
            ) : rankedCountries.length === 0 ? (
              <StatusMessage />
            ) : (
              <table>
                <colgroup>
                  <col width="5%" />
                  <col width="5%" />
                  <col width="5%" />
                  <col width="5%" />
                  <col width="5%" />
                  <col width="15%" />
                  <col width="15%" />
                  <col width="15%" />
                  <col width="15%" />
                  <col width="15%" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t("table.rank")}</th>
                    <th>{t("table.country")}</th>
                    <th style={{ fontSize: "25px" }}>🥇</th>
                    <th style={{ fontSize: "25px" }}>🥈</th>
                    <th style={{ fontSize: "25px" }}>🥉</th>
                    <th>145m</th>
                    <th>90m</th>
                    <th>70m</th>
                    <th>30m</th>
                    <th>{t("table.total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rankedCountries.slice(0, 10).map((country, index) => {
                    const rank = getRankWithTopThree(rankedCountries, index);
                    return (
                      <tr key={index} className={index < 3 ? "ranking" : ""}>
                        <td>{rank}</td>
                        <td className="flag-overall">
                          <img
                            className="flag"
                            src={findCountryFlag(country.country)}
                            alt={country.country}
                          />
                          {country.country}
                        </td>
                        <td>{country.gold}</td>
                        <td>{country.silver}</td>
                        <td>{country.bronze}</td>
                        <td>{country.scores["145M"] || "-"}</td>
                        <td>{country.scores["90M"] || "-"}</td>
                        <td>{country.scores["70M"] || "-"}</td>
                        <td>{country.scores["30M"] || "-"}</td>
                        <td>{country.total}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <div className="table-gradient"></div>
        </div>
      </div>
    </>
  );
};
export default NationalView;
